import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { MaintenanceArea } from './areas';
import { expectedBackAt, partitionNotices } from './logic';
import {
  EMPTY_ENTRY,
  MaintenanceEntry,
  SITE_WIDE_KEY,
  getEntry,
  refreshArea,
  retainArea,
  subscribeToStore,
} from './store';
import { MaintenanceNotice } from './types';

export interface MaintenanceView {
  /** True once the first status call has settled (successfully or not). */
  loaded: boolean;
  /** An active blocking window covers this area (or the whole product). */
  blocked: boolean;
  blocking: MaintenanceNotice[];
  banners: MaintenanceNotice[];
  upcoming: MaintenanceNotice[];
  /** When the area should be back, or null when unknown or already past. */
  expectedBackAt: string | null;
  /** Re-checks the status right away. */
  refresh: () => void;
}

type ViewData = Omit<MaintenanceView, 'refresh'>;

/** Pure derivation of what a gate shows from a cache entry. Exported for tests. */
export function deriveView(entry: MaintenanceEntry, area: string | null, now: number): ViewData {
  const notices = entry.notices ?? [];
  const { override } = entry;
  const overrideStillHolds =
    override !== null && (override.endsAt === null || Date.parse(override.endsAt) > now);
  const hasRealBlock = notices.some((n) => n.state === 'active' && n.mode === 'block');
  const all = overrideStillHolds && !hasRealBlock ? [...notices, override] : notices;

  const { blocking, banners, upcoming } = partitionNotices(all, area);
  return {
    loaded: entry.loaded,
    blocked: blocking.length > 0,
    blocking,
    banners,
    upcoming,
    expectedBackAt: expectedBackAt(blocking, now),
  };
}

/**
 * Maintenance status for one area (omit `area` for the site-wide, whole-product
 * view). Every caller for the same area shares one cached request; the status
 * refreshes every minute, on window focus and when an API call answers 503
 * MAINTENANCE. Fails open: until loaded, or if the status call fails, nothing
 * is blocked and nothing is shown.
 */
export function useMaintenance(area?: MaintenanceArea): MaintenanceView {
  const key = area ?? SITE_WIDE_KEY;

  useEffect(() => retainArea(key), [key]);

  const entry = useSyncExternalStore(
    subscribeToStore,
    () => getEntry(key),
    () => EMPTY_ENTRY,
  );
  const refresh = useCallback(() => {
    void refreshArea(key, true);
  }, [key]);

  const view = useMemo(() => deriveView(entry, area ?? null, Date.now()), [entry, area]);
  return { ...view, refresh };
}
